import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Badge } from "@nosible/shared/components/ui/badge";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import {
  ArrowLeft,
  BarChart,
} from "lucide-react";
import { AccountPageHeader } from "@/account/components/AccountPageHeader";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "@nosible/shared/components/ui/chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

type UsageEntry = {
  type: string;
  limit: number;
  used: number;
  lastUsed: string | null;
};

export function SubPeriodPage() {
  const navigate = useNavigate();
  const { subscriptionId, periodId } = useParams<{
    subscriptionId: string;
    periodId: string;
  }>();
  const { data: session } = useSession();

  const {
    data: subscription,
    isLoading: subLoading,
    error: subError,
  } = trpc.subscription.getSubscription.useQuery(
    { subscriptionId: subscriptionId! },
    { enabled: !!session && !!subscriptionId }
  );

  const {
    data: period,
    isLoading: periodLoading,
    error: periodError,
  } = trpc.subscription.usage.periodUsage.useQuery(
    { subscriptionId: subscriptionId!, periodId: periodId! },
    { enabled: !!session && !!subscriptionId && !!periodId }
  );

  const isCurrentPeriod = period?.isCurrent ?? false;

  useEffect(() => {
    if (!session) {
      navigate("/login");
    }
  }, [session, navigate]);

  if (subLoading || periodLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  const error = subError || periodError;
  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error.message}</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  if (!subscription || !period) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Period not found</div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  const usageEntries = Array.isArray(period.usage)
    ? (period.usage as UsageEntry[])
    : (period.usage as any)?.splits || [];

  return (
    <div className="account-ui-page">
      <AccountPageHeader
        title="Period Details"
        description={
          <>
            {new Date(period.startDate).toLocaleDateString()} -{" "}
            {new Date(period.endDate).toLocaleDateString()}
          </>
        }
        icon={BarChart}
        backTo={`/subscriptions/${subscriptionId}/periods`}
        backLabel="Back to All Periods"
        actions={isCurrentPeriod ? <Badge variant="default">Current Period</Badge> : undefined}
      />

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Usage Summary</CardTitle>
            <CardDescription>
              Current usage for this billing period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UsageBars usage={usageEntries} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Usage Over Time</CardTitle>
            <CardDescription>
              Usage trends throughout this billing period. Horizontal lines show limits.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UsageChart
              usage={usageEntries}
              startDate={new Date(period.startDate)}
              endDate={new Date(period.endDate)}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function UsageBars({ usage }: { usage: UsageEntry[] }) {
  if (usage.length === 0) {
    return (
      <div className="text-center py-4 text-chromium-mist">
        No usage data available for this period
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {usage.map((usageEntry) => {
        const percentage =
          usageEntry.limit > 0 ? (usageEntry.used / usageEntry.limit) * 100 : 0;
        const remaining = usageEntry.limit - usageEntry.used;

        return (
          <div key={usageEntry.type} className="space-y-2 p-4 bg-slate-core/30 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-silica capitalize">
                {usageEntry.type}
              </span>
              <span className="text-sm font-bold text-silica">
                {Math.round(percentage)}%
              </span>
            </div>
            <div className="h-3 bg-slate-core rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${percentage > 90
                  ? "bg-red-500"
                  : percentage > 70
                    ? "bg-yellow-500"
                    : "bg-neon-green"
                  }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-chromium-mist">
              <span>
                {usageEntry.used.toLocaleString()} / {usageEntry.limit.toLocaleString()}
              </span>
              <span>{remaining.toLocaleString()} left</span>
            </div>
            {usageEntry.lastUsed && (
              <div className="text-xs text-chromium-mist">
                Last used: {new Date(usageEntry.lastUsed).toLocaleString()}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function buildChartConfig(usage: UsageEntry[]): ChartConfig {
  const config: ChartConfig = {};
  usage.forEach((entry) => {
    config[entry.type] = {
      label: entry.type.charAt(0).toUpperCase() + entry.type.slice(1),
      color: "rgb(200 200 204)",
    };
  });
  return config;
}

function UsageChart({
  usage,
  startDate,
  endDate,
}: {
  usage: UsageEntry[];
  startDate: Date;
  endDate: Date;
}) {
  if (usage.length === 0) {
    return (
      <div className="text-center py-8 text-chromium-mist">
        No usage data to display
      </div>
    );
  }

  const chartConfig = useMemo(() => buildChartConfig(usage), [usage]);

  const chartData = useMemo(() => {
    const data: Array<{
      date: string;
      timestamp: number;
      [key: string]: string | number;
    }> = [];

    const start = startDate.getTime();
    const end = endDate.getTime();
    const now = Date.now();
    const duration = end - start;
    const numPoints = 10;

    for (let i = 0; i <= numPoints; i++) {
      const timestamp = start + (duration * i) / numPoints;
      const date = new Date(timestamp);

      const point: (typeof data)[0] = {
        date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        timestamp,
      };

      usage.forEach((entry) => {
        const lastUsedTime = entry.lastUsed ? new Date(entry.lastUsed).getTime() : 0;
        // For future timestamps (after now), don't show any usage data
        if (timestamp > now) {
          // Leave undefined - no data point for future
          return;
        }
        if (timestamp >= lastUsedTime && lastUsedTime > 0) {
          point[entry.type] = entry.used;
        } else if (timestamp >= now) {
          // Current point - show current usage
          point[entry.type] = entry.used;
        } else {
          // Interpolate based on progress toward current time
          const progressToNow = Math.min((timestamp - start) / (now - start), 1);
          point[entry.type] = Math.round(entry.used * progressToNow);
        }
      });

      data.push(point);
    }

    return data;
  }, [usage, startDate, endDate]);

  const limits = useMemo(() => {
    return usage.reduce(
      (acc, entry) => {
        acc[entry.type] = entry.limit;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [usage]);

  const maxValue = useMemo(() => {
    const maxUsed = Math.max(...usage.map((u) => u.used), 0);
    const maxLimit = Math.max(...usage.map((u) => u.limit), 0);
    return Math.max(maxUsed, maxLimit) * 1.1;
  }, [usage]);

  const usageTypes = usage.map((u) => u.type);

  return (
    <ChartContainer config={chartConfig} className="min-h-[300px] w-full">
      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-slate-core)" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          stroke="var(--color-chromium-mist)"
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          stroke="var(--color-chromium-mist)"
          domain={[0, maxValue]}
          tickFormatter={(value) => value.toLocaleString()}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />

        {usageTypes.map((type) => (
          <ReferenceLine
            key={`limit-${type}`}
            y={limits[type]}
            stroke={chartConfig[type]?.color || "gray"}
            strokeDasharray="5 5"
            strokeOpacity={0.5}
            label={{
              value: `${type} limit`,
              position: "right",
              fill: chartConfig[type]?.color || "gray",
              fontSize: 10,
            }}
          />
        ))}

        {usageTypes.map((type) => (
          <Line
            key={type}
            type="monotone"
            dataKey={type}
            stroke={chartConfig[type]?.color || "gray"}
            strokeWidth={2}
            dot={{ fill: chartConfig[type]?.color || "gray", r: 4 }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}
